/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { AddIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAuthState } from '../../../../../auth/AuthProvider'
import { useAccount } from '../../../account/AccountProvider'
import { useRole } from '../../../../../providers/RoleProvider'
import {
  type Appointment,
  type AppointmentSettings,
  type AppointmentStatus
} from '../../../../../model/appointment'
import { getAppointmentService } from '../../../../../services/Services'
import {
  clinicDateTimeToTimestamp,
  timestampToCalendarDate
} from '../../../../../utils/appointmentUtils'
import AppointmentModal from '../AppointmentModal'

const STATUSES: AppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'checked-in',
  'completed',
  'cancelled',
  'no-show'
]

const STATUS_STYLES: Record<
  AppointmentStatus,
  { color: string; background: string; border: string }
> = {
  scheduled: { color: '#1d4ed8', background: '#eff6ff', border: '#bfdbfe' },
  confirmed: { color: '#047857', background: '#ecfdf5', border: '#a7f3d0' },
  'checked-in': {
    color: '#6d28d9',
    background: '#f5f3ff',
    border: '#ddd6fe'
  },
  completed: { color: '#475569', background: '#f8fafc', border: '#cbd5e1' },
  cancelled: { color: '#b91c1c', background: '#fef2f2', border: '#fecaca' },
  'no-show': { color: '#c2410c', background: '#fff7ed', border: '#fed7aa' }
}

const ARABIC_CALENDAR_LOCALE = {
  code: 'ar',
  direction: 'rtl' as const,
  weekText: 'أسبوع',
  allDayText: 'اليوم كله',
  noEventsText: 'لا توجد مواعيد'
}

function localDateKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getUTCDate()).padStart(2, '0')}`
}

interface Props {
  settings: AppointmentSettings
  initialDate?: string
}

export default function CalendarCore({ settings, initialDate }: Props) {
  const { uid } = useAuthState()
  const { doctorId } = useRole()
  const { locale } = useAccount()
  const { formatMessage } = useIntl()
  const modal = useDisclosure()
  const ownerId = doctorId ?? uid ?? ''
  const actorId = uid ?? ''
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [visibleRange, setVisibleRange] = useState<{
    start: number
    end: number
  } | null>(null)
  const [selected, setSelected] = useState<Appointment | null>(null)
  const [initialStart, setInitialStart] = useState<number | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [visibleStatuses, setVisibleStatuses] =
    useState<AppointmentStatus[]>(STATUSES)

  useEffect(() => {
    if (!ownerId || !visibleRange) return
    setLoading(true)
    setError(null)
    return getAppointmentService().subscribeToRange(
      ownerId,
      visibleRange.start,
      visibleRange.end,
      (updated) => {
        setAppointments(updated)
        setLoading(false)
      },
      () => {
        setError(formatMessage({ id: 'calendar.loadError' }))
        setLoading(false)
      }
    )
  }, [formatMessage, ownerId, visibleRange])

  const events = useMemo(
    () =>
      appointments
        .filter((appointment) => visibleStatuses.includes(appointment.status))
        .map((appointment) => ({
          id: appointment.id,
          title: appointment.patientName,
          start: timestampToCalendarDate(appointment.start, settings.timezone),
          end: timestampToCalendarDate(
            appointment.start + appointment.duration * 60_000,
            settings.timezone
          ),
          backgroundColor: STATUS_STYLES[appointment.status].background,
          borderColor: STATUS_STYLES[appointment.status].border,
          textColor: STATUS_STYLES[appointment.status].color,
          extendedProps: {
            status: appointment.status,
            duration: appointment.duration,
            visitType: appointment.visitType
          }
        })),
    [appointments, settings.timezone, visibleStatuses]
  )

  const businessHours = useMemo(
    () =>
      Object.entries(settings.weeklyHours)
        .filter(([, hours]) => hours.enabled)
        .map(([day, hours]) => ({
          daysOfWeek: [Number(day)],
          startTime: hours.start,
          endTime: hours.end
        })),
    [settings.weeklyHours]
  )

  const visibleClinicHours = useMemo(() => {
    const enabledHours = Object.values(settings.weeklyHours).filter(
      (hours) => hours.enabled
    )
    if (enabledHours.length === 0) {
      return { start: '08:00:00', end: '18:00:00' }
    }
    const starts = enabledHours.map((hours) => hours.start).sort()
    const ends = enabledHours.map((hours) => hours.end).sort()
    return {
      start: `${starts[0]}:00`,
      end: `${ends[ends.length - 1]}:00`
    }
  }, [settings.weeklyHours])

  const calendarNow = useCallback(
    () => timestampToCalendarDate(Date.now(), settings.timezone),
    [settings.timezone]
  )

  const openNew = (start?: number) => {
    setSelected(null)
    setInitialStart(start)
    modal.onOpen()
  }

  return (
    <Box display="flex" flexDirection="column" h="full" minH={0}>
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={2}
        mb={2}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="10px"
        p={2}
        boxShadow="0 1px 2px rgba(15, 23, 42, 0.04)"
      >
        <Flex
          align={{ base: 'stretch', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={2}
          minW={0}
        >
          <Text
            flexShrink={0}
            fontSize="10px"
            fontWeight="700"
            color="gray.500"
            letterSpacing="0.08em"
            textTransform="uppercase"
          >
            {formatMessage({ id: 'calendar.filters' })}
          </Text>
          <HStack spacing={1} flex={1} minW={0} flexWrap="wrap">
            {STATUSES.map((status) => {
              const isChecked = visibleStatuses.includes(status)
              const count = appointments.filter(
                (appointment) => appointment.status === status
              ).length

              return (
                <Checkbox
                  key={status}
                  size="sm"
                  isChecked={isChecked}
                  aria-label={formatMessage({
                    id: `calendar.status.${status}`
                  })}
                  px={2}
                  py={1}
                  m={0}
                  borderWidth="1px"
                  borderColor={isChecked ? 'gray.300' : 'gray.200'}
                  borderRadius="full"
                  bg={isChecked ? 'gray.50' : 'white'}
                  color={isChecked ? 'gray.800' : 'gray.500'}
                  transition="all 0.15s ease"
                  _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                  sx={{
                    '.chakra-checkbox__control': { display: 'none' },
                    '.chakra-checkbox__label': { marginInlineStart: 0 }
                  }}
                  onChange={(event) => {
                    setVisibleStatuses((current) =>
                      event.target.checked
                        ? [...current, status]
                        : current.filter((item) => item !== status)
                    )
                  }}
                >
                  <HStack spacing={1.5}>
                    <Box
                      boxSize="7px"
                      borderRadius="full"
                      bg={STATUS_STYLES[status].color}
                      opacity={isChecked ? 1 : 0.35}
                    />
                    <Text as="span" fontSize="xs" fontWeight="600">
                      {formatMessage({ id: `calendar.status.${status}` })}
                    </Text>
                    <Badge
                      minW="18px"
                      px={1}
                      borderRadius="full"
                      bg={isChecked ? 'gray.200' : 'gray.100'}
                      color="gray.600"
                      textAlign="center"
                      fontSize="9px"
                    >
                      {count}
                    </Badge>
                  </HStack>
                </Checkbox>
              )
            })}
          </HStack>
        </Flex>
        <Button
          leftIcon={<AddIcon boxSize="10px" />}
          bg="gray.900"
          color="white"
          size="xs"
          flexShrink={0}
          borderRadius="8px"
          _hover={{ bg: 'gray.700' }}
          _active={{ bg: 'black' }}
          onClick={() => {
            openNew()
          }}
        >
          {formatMessage({ id: 'calendar.newAppointment' })}
        </Button>
      </Flex>

      {error && (
        <Alert status="error" mb={2} py={2}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      <Box
        position="relative"
        flex={1}
        minH={0}
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="10px"
        p={{ base: 1, md: 2 }}
        overflowX="auto"
        boxShadow="0 1px 3px rgba(15, 23, 42, 0.06)"
        sx={{
          '.fc': {
            minW: 0,
            fontSize: '14px',
            color: 'gray.700'
          },
          '.fc .fc-toolbar': { mb: 2, gap: 2, flexWrap: 'wrap' },
          '.fc .fc-toolbar-chunk': {
            display: 'flex',
            alignItems: 'center',
            gap: 1
          },
          '.fc .fc-toolbar-title': {
            fontSize: { base: 'md', md: 'lg' },
            fontWeight: '700',
            letterSpacing: '-0.02em',
            color: 'gray.900'
          },
          '.fc .fc-button': {
            bg: 'white',
            color: 'gray.600',
            borderColor: 'gray.200',
            borderRadius: '7px',
            boxShadow: 'none',
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: 1.2,
            padding: '5px 8px',
            textTransform: 'capitalize'
          },
          '.fc .fc-button:hover': {
            bg: 'gray.50',
            color: 'gray.900',
            borderColor: 'gray.300'
          },
          '.fc .fc-button:focus': { boxShadow: 'none' },
          '.fc .fc-button-primary:disabled': {
            bg: 'gray.100',
            color: 'gray.400',
            borderColor: 'gray.200'
          },
          '.fc .fc-button-primary:not(:disabled).fc-button-active': {
            bg: 'gray.900',
            color: 'white',
            borderColor: 'gray.900'
          },
          '.fc-theme-standard .fc-scrollgrid': {
            borderColor: 'gray.200',
            borderRadius: '8px',
            overflow: 'hidden'
          },
          '.fc-theme-standard th': {
            bg: 'gray.50',
            borderColor: 'gray.200'
          },
          '.fc-theme-standard td': { borderColor: 'gray.100' },
          '.fc .fc-col-header-cell-cushion': {
            py: 1.5,
            color: 'gray.600',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.04em',
            textTransform: 'uppercase'
          },
          '.fc .fc-daygrid-day-number': {
            p: 2,
            color: 'gray.600',
            fontSize: '12px',
            fontWeight: '600'
          },
          '.fc .fc-timegrid-slot': { height: '2.15em' },
          '.fc .fc-timegrid-slot-label-cushion': {
            color: 'gray.400',
            fontSize: '10px',
            fontWeight: '600'
          },
          '.fc .fc-day-today': { bg: '#f8fafc !important' },
          '.fc .fc-timegrid-now-indicator-line': {
            borderColor: '#ef4444'
          },
          '.fc .fc-timegrid-now-indicator-arrow': {
            borderColor: '#ef4444',
            borderTopColor: 'transparent',
            borderBottomColor: 'transparent'
          },
          '.fc-event': {
            cursor: 'pointer',
            borderRadius: '7px',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
            transition: 'transform 0.12s ease, box-shadow 0.12s ease'
          },
          '.fc-event:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 8px rgba(15, 23, 42, 0.12)'
          },
          '.fc .fc-list': {
            borderColor: 'gray.200',
            borderRadius: '8px',
            overflow: 'hidden'
          },
          '.fc .fc-list-day-cushion': { bg: 'gray.50' }
        }}
      >
        {loading && (
          <Flex
            position="absolute"
            inset={0}
            bg="whiteAlpha.800"
            zIndex={2}
            align="center"
            justify="center"
          >
            <Spinner />
          </Flex>
        )}
        <FullCalendar
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
            interactionPlugin
          ]}
          timeZone="UTC"
          now={calendarNow}
          initialView={
            window.matchMedia('(max-width: 767px)').matches
              ? 'timeGridDay'
              : 'timeGridWeek'
          }
          initialDate={initialDate}
          locale={locale === 'ar' ? ARABIC_CALENDAR_LOCALE : 'en'}
          direction={locale === 'ar' ? 'rtl' : 'ltr'}
          firstDay={6}
          height="100%"
          stickyHeaderDates
          expandRows
          nowIndicator
          selectable
          selectMirror
          allDaySlot={false}
          businessHours={businessHours}
          events={events}
          eventContent={(info) => {
            const duration = Number(info.event.extendedProps.duration)
            return (
              <Box px={1.5} py={1} overflow="hidden">
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  lineHeight="short"
                  noOfLines={1}
                >
                  {info.event.title}
                </Text>
                <Text mt={0.5} fontSize="9px" opacity={0.8} noOfLines={1}>
                  {info.timeText}
                  {Number.isFinite(duration) ? ` · ${duration}m` : ''}
                </Text>
              </Box>
            )
          }}
          slotMinTime={visibleClinicHours.start}
          slotMaxTime={visibleClinicHours.end}
          scrollTime={visibleClinicHours.start}
          headerToolbar={{
            start: 'prev,next today',
            center: 'title',
            end: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
          }}
          buttonText={{
            today: formatMessage({ id: 'calendar.today' }),
            month: formatMessage({ id: 'calendar.month' }),
            week: formatMessage({ id: 'calendar.week' }),
            day: formatMessage({ id: 'calendar.day' }),
            list: formatMessage({ id: 'calendar.agenda' })
          }}
          datesSet={(info) => {
            const nextRange = {
              start: clinicDateTimeToTimestamp(
                localDateKey(info.start),
                '00:00',
                settings.timezone
              ),
              end: clinicDateTimeToTimestamp(
                localDateKey(info.end),
                '00:00',
                settings.timezone
              )
            }
            setVisibleRange((current) =>
              current?.start === nextRange.start &&
              current.end === nextRange.end
                ? current
                : nextRange
            )
          }}
          select={(info) => {
            const selectedDate = info.start
            const date = localDateKey(selectedDate)
            const time = info.allDay
              ? (settings.weeklyHours[selectedDate.getUTCDay()]?.start ??
                '09:00')
              : `${String(selectedDate.getUTCHours()).padStart(2, '0')}:${String(
                  selectedDate.getUTCMinutes()
                ).padStart(2, '0')}`
            openNew(clinicDateTimeToTimestamp(date, time, settings.timezone))
          }}
          eventClick={(info) => {
            const appointment = appointments.find(
              (item) => item.id === info.event.id
            )
            if (appointment) {
              setSelected(appointment)
              setInitialStart(undefined)
              modal.onOpen()
            }
          }}
          noEventsContent={formatMessage({ id: 'calendar.empty' })}
        />
      </Box>

      {ownerId && actorId && (
        <AppointmentModal
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          ownerId={ownerId}
          actor={{ uid: actorId }}
          settings={settings}
          appointment={selected}
          initialStart={initialStart}
        />
      )}
    </Box>
  )
}
